import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Filter } from 'src/app/core/interfaces/filter.interface';
import { UserRegistration } from 'src/app/core/interfaces/user-credentials.interface';
import { User } from 'src/app/core/interfaces/user.interface';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient, private router: Router) { }
  getUser(id: number): Observable<any> {
    return this.http.get(`${this.baseUrl + '/User'}` + '/' + id);
  }

  listUsers(PageNumber = 1, PageSize = 10, Term = '', filters?: Filter[]): Observable<any> {
    const params = {
      PageNumber,
      PageSize,
      Term,
      AdvancedFilter: filters
    };
    return this.http.post(`${this.baseUrl + '/User/List'}`, params);
  }

  selfRegister(data: UserRegistration) {
    return this.http.post(
      `${environment.apiBaseUrl}/User/SelfRegistr`,
      data
    )
  }

  createUser(data: User): Observable<any> {
    return this.http.post(`${this.baseUrl + '/User'}`, data);
  }

  updateUser(data: any): Observable<any> {
    return this.http.put(`${this.baseUrl + '/User'}`, data);
  }

  deleteUser(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl + '/User'}` + '/' + id);
  }

  getUserProfile(): Observable<any> {
    return this.http.get(`${this.baseUrl + '/User/Profile'}`);
  }

  updateUserProfile(data: any): Observable<any> {

    return this.http.put(`${this.baseUrl + '/User/profile'}`, data);
  }

  // Permission Management Methods
  getUserBranchPermissions(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/branch-permissions`);
  }

  updateUserBranchPermissions(userId: number, permissions: {[key: string]: boolean}): Observable<any> {
    return this.http.put(`${this.baseUrl}/User/${userId}/branch-permissions`, { 
      userId: userId,
      branchPermissions: permissions 
    });
  }

  getUserStorePermissions(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/store-permissions`);
  }

  getUserStorePermissionsList(userId: number,branchId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/store-permissions/list/${branchId}`);
  }

  updateUserStorePermissions(userId: number, permissions: {[key: string]: boolean}): Observable<any> {
    return this.http.put(`${this.baseUrl}/User/${userId}/store-permissions`, { 
      userId: userId,
      storePermissions: permissions 
    });
  }

  getUserCurrencyPermissions(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/currency-permissions`);
  }

  getUserCurrencyPermissionsList(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/currency-permissions/list`);
  }

  getUserProductGroupPermissionsList(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/product-group-permissions/list`);
  }

  getUserProductGroupPermissions(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/product-group-permissions`);
  }

  getUserCustomerPermissionsList(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/customer-permissions/list`);
  }

  getUserCustomerPermissions(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/customer-permissions`);
  }

  getUserVendorPermissionsList(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/vendor-permissions/list`);
  }

  getUserVendorPermissions(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/vendor-permissions`);
  }

  getUserCustomerPermissionsBase(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/customer-permissions/base-customers`);
  }

  getUserVendorPermissionsBase(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/vendor-permissions/base-vendors`);
  }

  getUserPaymentMethodDefinitionPermissionsList(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/payment-method-definition-permissions/list`);
  }

  getUserPaymentMethodDefinitionPermissions(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/payment-method-definition-permissions`);
  }

  getUserBoxPermissionsList(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/box-permissions/list`);
  }

  getUserBoxPermissions(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/box-permissions`);
  }

  getUserBankPermissionsList(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/bank-permissions/list`);
  }

  getUserBankPermissions(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/bank-permissions`);
  }

  getUserOtherAccountPermissionsList(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/other-account-permissions/list`);
  }

  getUserOtherAccountPermissions(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/other-account-permissions`);
  }

  updateUserProductGroupPermissions(userId: number, permissions: { [key: string]: boolean }): Observable<any> {
    return this.http.put(`${this.baseUrl}/User/${userId}/product-group-permissions`, {
      userId: userId,
      productGroupPermissions: permissions
    });
  }

  updateUserCustomerPermissions(userId: number, permissions: { [key: string]: boolean }): Observable<any> {
    return this.http.put(`${this.baseUrl}/User/${userId}/customer-permissions`, {
      userId: userId,
      Permissions: permissions
    });
  }

  updateUserVendorPermissions(userId: number, permissions: { [key: string]: boolean }): Observable<any> {
    return this.http.put(`${this.baseUrl}/User/${userId}/vendor-permissions`, {
      userId: userId,
      Permissions: permissions
    });
  }

  updateUserPaymentMethodDefinitionPermissions(userId: number, permissions: { [key: string]: boolean }): Observable<any> {
    return this.http.put(`${this.baseUrl}/User/${userId}/payment-method-definition-permissions`, {
      userId: userId,
      tenantPaymentMethodPermissions: permissions
    });
  }

  updateUserBoxPermissions(userId: number, permissions: { [key: string]: boolean }): Observable<any> {
    return this.http.put(`${this.baseUrl}/User/${userId}/box-permissions`, {
      userId: userId,
      Permissions: permissions
    });
  }

  updateUserBankPermissions(userId: number, permissions: { [key: string]: boolean }): Observable<any> {
    return this.http.put(`${this.baseUrl}/User/${userId}/bank-permissions`, {
      userId: userId,
      Permissions: permissions
    });
  }

  updateUserOtherAccountPermissions(userId: number, permissions: { [key: string]: boolean }): Observable<any> {
    return this.http.put(`${this.baseUrl}/User/${userId}/other-account-permissions`, {
      userId: userId,
      Permissions: permissions
    });
  }

  updateUserCurrencyPermissions(userId: number, permissions: {[key: string]: boolean}): Observable<any> {
    return this.http.put(`${this.baseUrl}/User/${userId}/currency-permissions`, { 
      userId: userId,
      currencyPermissions: permissions 
    });
  }

  updateSingleActiveBranch(userId: number, branchId: number): Observable<any> {
    return this.http.put(`${this.baseUrl}/User/branch-permissions/update-single`, {
      userId: userId,
      branchId: branchId,
      isActive: true,
    });
  }

  getUserBranchPermissionsList(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/User/${userId}/branch-permissions/list`);
  }

  /**
   * Returns permitted branch IDs using GET /User/{userId}/branch-permissions/list.
   * Use for "single vs multiple branch" check and for modal allowedBranchIds.
   */
  getPermittedBranchIdsFromList(userId: number): Observable<number[]> {
    return this.getUserBranchPermissionsList(userId).pipe(
      map((res: any) => {
        const ids: number[] = [];
        if (res?.data?.branchPermissions && Array.isArray(res.data.branchPermissions)) {
          res.data.branchPermissions.forEach((b: any) => {
            const id = b.branchId ?? b.branch?.id;
            if (id != null && id !== 0) ids.push(Number(id));
          });
        } else if (res?.data?.branches && Array.isArray(res.data.branches)) {
          const items = res.data.branches;
          const hasIsGranted = items.some((i: any) => i.isGranted === true || i.isGranted === false);
          items.forEach((item: any) => {
            if (hasIsGranted && item.isGranted !== true) return;
            const branch = item.branch ?? item;
            const id = branch?.id;
            if (id != null && id !== 0) ids.push(Number(id));
          });
        }
        return ids;
      })
    );
  }
}
